import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { CalendarClock, Car, CreditCard } from 'lucide-react'
import { formatCents } from '../../lib/money'
import { hhmm, prettyDayShort } from '../../lib/format'
import type { Locale, ReservationListItem } from '../../lib/api/types'
import { cn } from '../../lib/cn'

function statusStyle(status: string): string {
  const s = (status || '').toUpperCase()
  if (s.startsWith('CANCEL')) return 'bg-brand-100 text-brand-700'
  if (s === 'DRAFT' || s === 'PENDING') return 'bg-amber-100 text-amber-700'
  return 'bg-green-100 text-green-700'
}
function statusLabel(t: (k: string) => string, status: string): string {
  const s = (status || '').toUpperCase()
  if (s.startsWith('CANCEL')) return t('bookings.statusCancelled')
  if (s === 'DRAFT' || s === 'PENDING') return t('bookings.statusDraft')
  return t('bookings.statusConfirmed')
}

/** Perforated "ticket" card — shared by Le mie prenotazioni and the homepage shortcut so the
 *  design stays identical in both places. */
export function ReservationCard({ r, locale, onOpen }: { r: ReservationListItem; locale: Locale; onOpen: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onOpen}
      className="relative flex w-full items-stretch overflow-hidden rounded-2xl bg-white text-left shadow-card ring-1 ring-ink-100 transition hover:shadow-card-hover"
    >
      {/* left: code + status, then details */}
      <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-bold text-ink-900">{r.code}</span>
          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', statusStyle(r.status))}>
            {statusLabel(t, r.status)}
          </span>
        </div>
        <div className="mt-3 space-y-2 text-sm text-ink-600">
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 flex-none text-ink-400" />
            {prettyDayShort(r.arrivalDay, locale)} {hhmm(r.arrivalTime)} → {prettyDayShort(r.departureDay, locale)}{' '}
            {hhmm(r.departureTime)}
          </span>
          <span className="flex items-center gap-2">
            <Car className="h-4 w-4 flex-none text-ink-400" />
            {r.transportData?.label ?? '—'}
            {r.customerData?.licensePlate ? ` · ${r.customerData.licensePlate}` : ''}
          </span>
          {r.paymentModeData?.label && (
            <span className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 flex-none text-ink-400" />
              {r.paymentModeData.label}
            </span>
          )}
        </div>
      </div>

      {/* perforation: dashed divider + punch-hole notches */}
      <div className="relative w-0 self-stretch">
        <span className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full bg-ink-50" />
        <span className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full bg-ink-50" />
        <span className="absolute inset-y-3 left-1/2 -translate-x-1/2 border-l-2 border-dashed border-ink-200" />
      </div>

      {/* right stub: price + QR */}
      <div className="flex min-w-[116px] flex-none flex-col items-center justify-center gap-2 bg-ink-50/40 px-4 py-4 sm:min-w-[128px] sm:px-5">
        <span className="font-display text-lg font-extrabold text-ink-900">{formatCents(r.totalPrice, locale)}</span>
        <span className="rounded-md border border-ink-100 bg-white p-1">
          <QRCodeSVG value={r.code} size={52} level="M" marginSize={0} />
        </span>
      </div>
    </button>
  )
}
